/**
 * Shared UI bits for the Rate Benchmarks pages (styles, small components,
 * formatting helpers). Visual language follows the Part Rate DB view:
 * plain tables, inline styles, teal accent.
 */
import React from 'react';
import { authenticatedApiCall } from '../../../utils/apiClient';

/** GET returning the parsed body (authenticatedApiCall returns the axios response). */
export const apiGet = async (instance, accounts, url) =>
  (await authenticatedApiCall(instance, accounts, 'GET', url)).data;

/** POST/PUT/DELETE returning the parsed body. */
export const apiSend = async (instance, accounts, method, url, body = null) =>
  (await authenticatedApiCall(instance, accounts, method, url, body)).data;

export const errMsg = (e) => e?.response?.data?.message || e?.message || 'Request failed';

export const ACCENT = '#0f766e';

export const SOURCE_LABELS = {
  PART_RATE_DB: 'Part Rate DB (OTT)',
  PART_RATE_DB_MCAA: 'Part Rate DB (MCAA col)',
  OTTAV_61075: 'Ottav 61075',
  OTTAV_LATEST: 'Ottav Latest',
  MCAA: 'MCAA',
  EST_BOOK: 'Estimating Book',
};

export const SOURCE_COLORS = {
  PART_RATE_DB: '#0f766e',
  PART_RATE_DB_MCAA: '#115e59',
  OTTAV_61075: '#1d4ed8',
  OTTAV_LATEST: '#7c3aed',
  MCAA: '#b45309',
  EST_BOOK: '#be123c',
};

export const CHART_COLORS = ['#0f766e', '#1d4ed8', '#b45309', '#be123c', '#7c3aed', '#0891b2', '#4d7c0f', '#a21caf'];

export const styles = {
  page: { padding: '12px 16px', fontSize: '0.85rem', color: '#1f2937' },
  headerRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 },
  title: { fontSize: '1.15rem', fontWeight: 700, color: '#111827', margin: 0 },
  tabBar: { display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: 12, flexWrap: 'wrap' },
  tab: (active) => ({
    padding: '7px 14px', border: 'none', background: 'none', cursor: 'pointer',
    fontSize: '0.85rem', fontWeight: active ? 700 : 500,
    color: active ? ACCENT : '#6b7280',
    borderBottom: active ? `3px solid ${ACCENT}` : '3px solid transparent',
    marginBottom: -2,
  }),
  card: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
    padding: '12px 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },
  statGrid: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem', background: '#fff' },
  th: {
    position: 'sticky', top: 0, background: '#f3f4f6', border: '1px solid #e5e7eb',
    padding: '5px 8px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap', zIndex: 2,
  },
  td: { border: '1px solid #eef0f2', padding: '4px 8px', whiteSpace: 'nowrap' },
  tdNum: { border: '1px solid #eef0f2', padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  input: {
    padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.8rem',
    background: '#fff',
  },
  select: {
    padding: '5px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.8rem',
    background: '#fff', maxWidth: 220,
  },
  btn: {
    padding: '6px 12px', border: `1px solid ${ACCENT}`, borderRadius: 6, cursor: 'pointer',
    background: ACCENT, color: '#fff', fontSize: '0.8rem', fontWeight: 600,
  },
  btnGhost: {
    padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer',
    background: '#fff', color: '#374151', fontSize: '0.8rem', fontWeight: 500,
  },
  badge: (bg, fg) => ({
    display: 'inline-block', padding: '1px 7px', borderRadius: 10, fontSize: '0.7rem',
    fontWeight: 600, background: bg, color: fg,
  }),
  scroll: { overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, maxHeight: '62vh' },
};

export function StatCard({ label, value, sub, color = ACCENT }) {
  return (
    <div style={{ ...styles.card, minWidth: 130, padding: '10px 14px' }}>
      <div style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color }}>{value ?? '—'}</div>
      {sub ? <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{sub}</div> : null}
    </div>
  );
}

export function Spinner({ label = 'Loading…' }) {
  return (
    <div style={{ padding: 30, textAlign: 'center', color: '#6b7280', fontSize: '0.85rem' }}>
      <span style={{ display: 'inline-block', width: 16, height: 16, border: '3px solid #e5e7eb', borderTopColor: ACCENT, borderRadius: '50%', animation: 'rbspin 0.8s linear infinite', verticalAlign: '-3px', marginRight: 8 }} />
      {label}
      <style>{'@keyframes rbspin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}

export function ErrorBox({ error, onRetry }) {
  return (
    <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: '0.85rem' }}>
      {String(error)}
      {onRetry ? <button style={{ ...styles.btnGhost, marginLeft: 10 }} onClick={onRetry}>Retry</button> : null}
    </div>
  );
}

export const fmtHours = (v) => (v === null || v === undefined ? '' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 3 }));
export const fmtRatio = (v) => (v === null || v === undefined ? '' : Number(v).toFixed(2));
export const fmtInt = (v) => (v === null || v === undefined ? '' : Number(v).toLocaleString());

/** Ratio badge coloured by deviation from 1.0 (±5% green, ±20% amber, else red). */
export function RatioBadge({ ratio }) {
  if (ratio === null || ratio === undefined) return null;
  const dev = Math.abs(ratio - 1);
  const [bg, fg] = dev <= 0.05 ? ['#dcfce7', '#166534'] : dev <= 0.2 ? ['#fef3c7', '#92400e'] : ['#fee2e2', '#991b1b'];
  return <span style={styles.badge(bg, fg)}>{ratio > 1 ? '+' : ''}{((ratio - 1) * 100).toFixed(0)}%</span>;
}

/** Cell for one source on a comparison row: hours + wildcard flags, gap, or dash. */
export function SourceCell({ cell }) {
  if (!cell || cell.status === 'not_covered') {
    return <span style={{ color: '#d1d5db' }}>—</span>;
  }
  if (cell.status === 'gap') {
    return <span style={styles.badge('#fef3c7', '#92400e')}>gap</span>;
  }
  return (
    <span title={cell.factorLabel ? `Derived via factor: ${cell.factorLabel}` : cell.schList ? `Averaged across schedules: ${cell.schList.join(', ')}` : undefined}>
      {fmtHours(cell.hours)}
      {cell.derived ? <sup style={{ color: '#be123c', fontWeight: 700 }}> f</sup> : null}
      {cell.schWildcard ? <sup style={{ color: '#6b7280' }}> *</sup> : null}
      {cell.schAggregated ? <sup style={{ color: '#6b7280' }}> ~</sup> : null}
      {cell.n > 1 ? <sup style={{ color: '#9ca3af' }}> ({cell.n})</sup> : null}
    </span>
  );
}
